import { SectionTitle } from "@/components/ui/Typography";
import { BRAND_COLORS } from "@/lib/brandColors";
import { Ionicons } from "@expo/vector-icons";
import React, { useEffect, useState } from "react";
import { AccessibilityInfo, Platform, Pressable } from "react-native";
import { Dialog, VisuallyHidden, XStack, YStack } from "tamagui";

type AppDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: number;
  /** Higher when opening above DnD / sheets. Default matches other app dialogs. */
  zIndex?: number;
  /**
   * Stable identity for portal nodes. Do NOT key by title — changing the key
   * remounts content and replays enter animation (looks like a jump on queue).
   */
  instanceKey?: string;
  /**
   * `standard` — title row + padded body.
   * `bare` — no title row; flush content (e.g. media modals).
   */
  chrome?: "standard" | "bare";
  /** When false, no X control (e.g. dismiss via CTA / overlay). Default true. */
  showClose?: boolean;
};

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (Platform.OS === "web" && typeof window !== "undefined") {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const sync = () => {
        if (mounted) setReduced(mq.matches);
      };
      sync();
      mq.addEventListener?.("change", sync);
      return () => {
        mounted = false;
        mq.removeEventListener?.("change", sync);
      };
    }

    void AccessibilityInfo.isReduceMotionEnabled().then((v) => {
      if (mounted) setReduced(v);
    });
    const sub = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      (v) => {
        if (mounted) setReduced(v);
      },
    );
    return () => {
      mounted = false;
      sub.remove();
    };
  }, []);

  return reduced;
}

/**
 * Shared compact dialog shell (title + close + content).
 * Same pattern as LessonShareModal — swap children per use case.
 */
export function AppDialog({
  open,
  onOpenChange,
  title,
  children,
  maxWidth = 480,
  zIndex = 500,
  instanceKey = "app-dialog",
  chrome = "standard",
  showClose = true,
}: AppDialogProps) {
  const reduceMotion = usePrefersReducedMotion();

  if (!open) return null;

  const close = () => onOpenChange(false);
  const bare = chrome === "bare";
  const anim = reduceMotion ? undefined : "quick";
  const enter = reduceMotion
    ? undefined
    : { opacity: 0, scale: 0.985, y: 6 };
  const exit = reduceMotion ? undefined : { opacity: 0, scale: 0.99 };

  const closeButton = showClose ? (
    <Pressable
      onPress={close}
      accessibilityRole="button"
      accessibilityLabel="Close"
      hitSlop={12}
      style={
        Platform.OS === "web"
          ? ({ cursor: "pointer" } as object)
          : undefined
      }
    >
      <YStack
        width={36}
        height={36}
        borderRadius={18}
        ai="center"
        jc="center"
        backgroundColor={bare ? "rgba(255,255,255,0.95)" : "transparent"}
        {...(bare
          ? Platform.OS === "web"
            ? ({
                boxShadow: "0 1px 3px rgba(13,13,13,0.08)",
              } as object)
            : {
                shadowColor: "#0D0D0D",
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.07,
                shadowRadius: 2,
                elevation: 1,
              }
          : {})}
      >
        <Ionicons name="close" size={bare ? 20 : 26} color={BRAND_COLORS.text} />
      </YStack>
    </Pressable>
  ) : null;

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      <Dialog.Portal zIndex={zIndex}>
        <Dialog.Overlay
          key={`${instanceKey}-overlay`}
          animation={anim}
          opacity={0.48}
          backgroundColor="rgba(13, 13, 13, 0.48)"
          enterStyle={reduceMotion ? undefined : { opacity: 0 }}
          exitStyle={reduceMotion ? undefined : { opacity: 0 }}
          onPress={close}
          zIndex={zIndex}
        />
        <Dialog.Content
          elevate
          key={`${instanceKey}-content`}
          animation={anim}
          enterStyle={enter}
          exitStyle={exit}
          backgroundColor={BRAND_COLORS.cardBg}
          borderRadius={bare ? 22 : 16}
          padding={bare ? 0 : 24}
          width="94%"
          maxWidth={maxWidth}
          gap={bare ? 0 : 20}
          overflow="hidden"
          zIndex={zIndex + 1}
          onPress={(e) => e.stopPropagation()}
          {...(Platform.OS === "web"
            ? ({
                boxShadow: bare
                  ? "0 20px 40px rgba(13, 13, 13, 0.14), 0 2px 8px rgba(13, 13, 13, 0.06)"
                  : "0 24px 48px rgba(13, 13, 13, 0.18)",
              } as object)
            : {
                shadowColor: "#0D0D0D",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: bare ? 0.16 : 0.22,
                shadowRadius: bare ? 22 : 28,
                elevation: bare ? 14 : 18,
              })}
        >
          <VisuallyHidden>
            <Dialog.Title>{title}</Dialog.Title>
          </VisuallyHidden>

          {bare ? (
            closeButton ? (
              <YStack position="absolute" top={12} right={12} zIndex={2}>
                {closeButton}
              </YStack>
            ) : null
          ) : (
            <XStack jc="space-between" ai="center" width="100%">
              <SectionTitle color={BRAND_COLORS.text} f={1} pr="$3">
                {title}
              </SectionTitle>
              {closeButton}
            </XStack>
          )}

          <YStack gap={bare ? 0 : 20} width="100%">
            {children}
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
